import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { LabelAndValue } from '../../../../../../global/components/data-display/label-value';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../../../../global/constants/theme/styles/components/buttonstyles';
import { planAdaptionTableStyles } from '../../../../../../Global/constants/theme/styles/tables/plan-adoption-table';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { useGpsType } from '../../../../../../global/hooks/useGpsType';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import DocusignView from '../../../docusign/docusign-view';
import { DocuSignRequest, DocuSignRequestOptions, DocuSignResult } from '../../../docusign/models/docusign-models';
import AdoptingInstitutionView from '../display-components/adopting-institution-view';
import { ContributionAmountType, EmployerContributionStatusType, LayDcPlanAdoptionContentModel, LayDcPlanFormModel, PlanFormModel, PlanSubmitRequest, QccoStatus } from '../models/plan-adoption-form-models';
import { PlanAdoptionRoutes } from '../routes';
import { QccoPlanAdoptionWizard } from '../wizards/qcco-plan-adoption-wizard';
import { PlanParticipantRowHeader } from './plan-participant-row-header';
import { PlanParticipantRowView } from './plan-participant-row-view';

interface Props {
    wizard: QccoPlanAdoptionWizard;
}

function QccoReview(props: Props) {

    const { wizard } = props;
    const content: LayDcPlanAdoptionContentModel = wizard?.getFormContentModel();
    const gpsItem = useGpsType(content?.gpsType);
    const apiErrors = useApiResponseError(wizard?.getFormType());
    const currentInstitution = wizard?.wizardOptions?.currentInstitution;
    const initialValues: LayDcPlanFormModel = wizard?.getFormInitialValue();
    const qccoStatus: QccoStatus = wizard?.calculateQccoStatus(initialValues);
    const validationSchema = wizard?.getFormValidationSchema();
    const labelSettings = wizard?.epiContent?.labelSettingState?.epiContent;

    const employerContributionStatusOptions = wizard?.getEmployerContributionStatusOptions();
    const memberTypeOptions = wizard?.getMemberTypeOptions();

    const hasEmployerContributionStatus = content?.hasEmployerContributionStatus;
    const hasMemberType = content?.hasMemberType;

    const planSubmitRequest: PlanSubmitRequest = {
        clientId: currentInstitution?.org_client_nbr,
        values: initialValues,
    };

    const adoptionDetailsLink = `${wizard?.wizardOptions?.featureRootPath}${PlanAdoptionRoutes.AdoptionDetails}`;
    const questionsLink = `${wizard?.wizardOptions?.featureRootPath}${PlanAdoptionRoutes.QccoQuestions}`;

    const tableClasses = planAdaptionTableStyles();
    const buttonClasses = buttonStyles();

    const [showDocusignView, setShowDocusignView] = useState(false);

    const docuSignRequest: DocuSignRequest = wizard?.prepareDocuSignRequest();

    const docuSignOptions: DocuSignRequestOptions = {
        request: docuSignRequest,
        finishText: content?.docuSignFinishText,
        rejectText: content?.docuSignRejectText,
        onDecline: () => {
            setShowDocusignView(false);
        },
        onSigningComplete: (result: DocuSignResult) => {

            const completeValues: PlanFormModel = {
                ...initialValues,
                envelopeId: result?.envelopeId,
            };

            wizard?.onSubmit({
                values: completeValues,
                clientId: currentInstitution?.org_client_nbr,
            });
        },
    };

    const qccoCalculatedStatus = wizard?.calculateQccoStatus(initialValues);

    const isIndemnificationRequired = wizard?.isIndemnificationRequired();
    const isIndemnificationComplete = wizard?.isIndemnificationComplete();
    const isComplyEpiscopalChurchLayPensionSystemQuestionRequired = wizard?.isComplyEpiscopalChurchLayPensionSystemQuestionRequired();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function onValidationSucess(request: PlanSubmitRequest): void {
        setShowDocusignView(true);
    }

    function onValidationFail(): void {
        window.scrollTo(0, 0);
    }

    function renderEditLink(path: string) {
        return (
            <button className={buttonClasses.linkButton} type='button' onClick={() => wizard?.navigateToPath(path, planSubmitRequest)}>{content.contributionEditLabel}</button>
        );
    }

    function renderQuestion(question: string, answer: string) {
        let displayText = '';
        if (answer === YesNoIndicators.Yes) {
            displayText = labelSettings?.yesText?.toUpperCase();
        } else if (answer === YesNoIndicators.No) {
            displayText = labelSettings?.noText?.toUpperCase();
        } else if (answer === YesNoIndicators.NotApplicable) {
            displayText = YesNoIndicators.NotApplicable;
        }
        
        return (
            <ContentSpacer>
                <div>{question}</div>
                <div><strong>{displayText}</strong></div>
            </ContentSpacer>
        );
    }

    function renderQCCOQualification() {

        if (qccoCalculatedStatus === QccoStatus.Qualified) {
            return (
                <>
                    <h4>{content?.qccoQualificationYesStatusTitle}</h4>
                    <WysiwygWithReactLink content={content?.qccoQualificationYesStatusText} />
                </>
            );
        }

        if (qccoCalculatedStatus === QccoStatus.NotQualified) {
            return (
                <>
                    <h4>{content?.qccoQualificationNoStatusTitle}</h4>
                    <WysiwygWithReactLink content={content?.qccoQualificationNoStatusText} />
                </>
            );
        }

        return null;
    }

    function renderMultipleContributionsParticipants(formProps: FormikProps<LayDcPlanFormModel>) {
        if (!formProps.values?.allow_multiple_contributions) return null;

        const participants = formProps.values?.participants?.filter((participant) => participant?.participant_name || participant?.contribution_type);

        if (participants?.length > 0) {
            return (
                <DividerSection>
                    <ContentSpacer>
                        <div className={`${tableClasses.tableWrapper} review-table`}>
                            <PlanParticipantRowHeader isReadonly wizard={wizard} />
                            <>
                                {
                                    participants?.map((participant, participantIndex) =>
                                        <PlanParticipantRowView
                                            key={`${participant.id}-${participantIndex}`}
                                            index={participantIndex}
                                            fieldName='participants'
                                            formProps={formProps}
                                            participant={participant}
                                            wizard={wizard}
                                            isReadonly />,
                                    )
                                }
                            </>
                        </div>
                    </ContentSpacer>
                </DividerSection>);
        }

        return null;
    }

    function renderMultipleContributions(formProps: FormikProps<LayDcPlanFormModel>) {
        if (qccoStatus !== QccoStatus.Qualified) return null;

        const allow_multiple_contributions = !!formProps.values?.allow_multiple_contributions;

        if (!allow_multiple_contributions) return null;

        return <>
            <ContentSpacer>
                <h3>{content?.adoptMultipleContributionTitle}</h3>
                <WysiwygWithReactLink content={content?.adoptMultipleContributionDescription} />
                {renderMultipleContributionsParticipants(formProps)}
            </ContentSpacer>
        </>;
    }

    function renderSalaryDeferralsContent() {
        return (
            <ContentSpacer>
                <WysiwygWithReactLink content={content?.adoptEmployeeSalaryDeferText} />
            </ContentSpacer>);
    }

    function renderMemberType() {
        if (!hasMemberType) return null;

        const memberValue = memberTypeOptions?.find((option) => option.value === initialValues?.member_type);

        return <LabelAndValue label={content?.adoptMemberTypeLabel} value={memberValue?.label} />;
    }

    function renderEmployerContributionStatus() {
        if (!hasEmployerContributionStatus) return null;

        const employerContributionStatusValue = employerContributionStatusOptions?.find((option) => option.value === initialValues?.employer_contribution_status);

        return <>
            <LabelAndValue label={content?.adoptEmployerContributionTitle} value={employerContributionStatusValue?.label} />
        </>;
    }

    function renderEmployerBaseContribution() {

        if (hasEmployerContributionStatus && initialValues?.employer_contribution_status === EmployerContributionStatusType.Discretionary) return null;

        if (initialValues?.employer_base_contribution_amount_type === ContributionAmountType.Percentage) {
            return <>
                <LabelAndValue label={content?.adoptEmployerBaseContributionLabel} value={`${initialValues?.employer_base_contribution_percentage}%`} />
            </>;
        }

        if (initialValues?.employer_base_contribution_amount_type === ContributionAmountType.Dollar) {
            return <>
                <LabelAndValue label={content?.adoptEmployerBaseContributionLabel} value={getCurrencyFormat(initialValues?.employer_base_contribution_amount)} />
            </>;
        }

        return null;
    }

    function renderEmployerMatchContribution() {

        if (hasEmployerContributionStatus && initialValues?.employer_contribution_status === EmployerContributionStatusType.Discretionary) return null;

        if (initialValues?.employer_match_contribution_amount_type === ContributionAmountType.Percentage) {
            return <>
                <LabelAndValue label={content?.adoptEmployerMatchContributionLabel} value={`${initialValues?.employer_match_contribution_percentage}%`} />
            </>;
        }

        if (initialValues?.employer_match_contribution_amount_type === ContributionAmountType.Dollar) {
            return <>
                <LabelAndValue label={content?.adoptEmployerMatchContributionLabel} value={getCurrencyFormat(initialValues?.employer_match_contribution_amount)} />
            </>;
        }

        return null;
    }

    function renderDocusignSignView() {
        if (!showDocusignView) return null;

        return <DocusignView options={docuSignOptions} />;
    }

    function renderComplyEpiscopalChurchLayPensionSystemQuestion() {

        if (!isComplyEpiscopalChurchLayPensionSystemQuestionRequired) return null;

        return (
            <ContentSpacer>
                <div>{content?.complyEpiscopalChurchLayPensionSystemQuestion}</div>
                <div><strong>{initialValues?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.Yes ? labelSettings?.yesText?.toUpperCase() : labelSettings?.noText?.toUpperCase()}</strong></div>
            </ContentSpacer>
        );
    }

    function renderIndemnificationStatus() {
        if (!isIndemnificationRequired || !isIndemnificationComplete) return null;

        return (
            <>
                {renderComplyEpiscopalChurchLayPensionSystemQuestion()}
                <DividerSection removeDivider sectionText={content?.indemnifTitle}>
                    <div><strong>{initialValues?.indemnificationEnvelopeId ? content?.indemnifySuccessLabel : content?.indemnifySkippedLabel}</strong></div>
                </DividerSection>
            </>
        );
    }

    if (!initialValues?.qccoQuestion1 || !qccoStatus || !isIndemnificationComplete) {
        return <Redirect to={wizard?.wizardOptions?.featureRootPath} />;
    }

    return (
        <PageContainer>
            <MainContent>
                <Formik
                    initialValues={initialValues}
                    onSubmit={
                        (values) => wizard?.onSubmit(
                            {
                                values,
                                clientId: currentInstitution?.org_client_nbr,
                            })
                    }
                    enableReinitialize
                    initialErrors={apiErrors}
                    validationSchema={validationSchema}
                >
                    {(formProps: FormikProps<LayDcPlanFormModel>) => (
                        <>
                            <Form>
                                <Grid>
                                    <FormWrapper
                                        formikProps={formProps}
                                        onCancel={() => wizard?.onCancel({
                                            values: formProps.values,
                                            clientId: currentInstitution?.org_client_nbr,
                                        })}
                                        submitDisabled={wizard?.isSubmitDisabled(formProps.values)}
                                        preventButtonDisableValidation={wizard?.preventButtonDisableValidation()}
                                        submitText={content?.submitLabel}
                                        cancelText={content?.cancelLabel}
                                        hideButtons
                                    >
                                        <PageHeader
                                            headerText={content?.pageTitle}
                                            description={content?.PageDescription}
                                        />
                                        <Hidden mdUp>
                                            <SideBarContent>
                                                <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                            </SideBarContent>
                                        </Hidden>
                                        <DividerSection removeDivider >
                                            {renderIndemnificationStatus()}

                                            <DividerSection removeDivider sectionText={content?.qccoQuestionTitle} secondarySectionNode={renderEditLink(questionsLink)}>
                                                {renderQuestion(content?.qccoQuestion1Label, initialValues.qccoQuestion1)}
                                                {renderQuestion(content?.qccoQuestion2Label, initialValues.qccoQuestion2)}
                                                {renderQuestion(content?.qccoQuestion3Label, initialValues.qccoQuestion3)}
                                            </DividerSection>

                                            <ContentSpacer>
                                                <h3>{content?.qccoQualificationStatusTitle}</h3>
                                                {renderQCCOQualification()}
                                                <WysiwygWithReactLink content={content?.qccoQualificationStatusText} />
                                            </ContentSpacer>

                                            <DividerSection removeDivider sectionText={content?.adoptDetailsTitle} secondarySectionNode={renderEditLink(adoptionDetailsLink)}>
                                                <AdoptingInstitutionView institution={currentInstitution} clientNumberLabel={content?.clientIdLabel} sectionTitle={content?.adoptingInstitutionLabel} />

                                                <LabelAndValue label={content?.adoptionDateLabel} value={initialValues?.adoption_date} />

                                                {renderSalaryDeferralsContent()}

                                                {renderMemberType()}

                                                <ContentSpacer>
                                                    <WysiwygWithReactLink content={content?.adoptEmployerContributionText} />
                                                </ContentSpacer>

                                                <LabelAndValue label={content?.adoptMinScheduleHoursLabel} value={initialValues?.minimum_scheduleds_hours} />
                                                <ContentSpacer>
                                                    <WysiwygWithReactLink content={content?.adoptMinScheduleHoursHelperText} />
                                                </ContentSpacer>

                                                {renderEmployerContributionStatus()}

                                                {renderEmployerBaseContribution()}

                                                {renderEmployerMatchContribution()}

                                                {renderMultipleContributions(formProps)}

                                            </DividerSection>

                                            <ContentSpacer>
                                                <h3>{content?.employerAuthorizationTitle}</h3>
                                                <WysiwygWithReactLink content={content?.employerAuthorizationDescription} />
                                            </ContentSpacer>

                                            {renderDocusignSignView()}

                                            {
                                                !showDocusignView &&
                                                <ContentSpacer>
                                                    <button className='btn-primary' type='button' onClick={() => wizard?.onValidateRequest({
                                                        values: initialValues,
                                                        clientId: currentInstitution?.org_client_nbr,
                                                    }, onValidationSucess, onValidationFail)}>
                                                        <span>{content?.signHereLabel}</span>
                                                    </button>
                                                </ContentSpacer>
                                            }
                                            
                                                
                                            <ContentSpacer>
                                                <WysiwygWithReactLink content={content?.signHereDescription} />
                                            </ContentSpacer>
                                            <button className='btn-secondary' type='button'
                                                onClick={() => wizard?.onCancel({
                                                    values: initialValues,
                                                    clientId: currentInstitution?.org_client_nbr,
                                                })}>{content?.cancelLabel}</button>
                                        </DividerSection>
                                    </FormWrapper>
                                </Grid>
                            </Form>
                        </>
                    )}
                </Formik>
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );

}

export default QccoReview;